import { logError } from "@core/lib/error-handling";

export interface Region {
  id: string;
  name: string;
  currency_code: string;
  currency_symbol: string;
}

export interface Price {
  product_id: string;
  amount: number;
  currency_symbol: string;
  lemonsqueezy_variant_id?: string;
}

// France only - simplified pricing
const FRANCE_REGION = {
  id: "FR",
  name: "France",
  currency_code: "EUR",
  currency_symbol: "€",
};

export const pricingService = {
  /**
   * Get the region for a given country code.
   * France only - always returns FR for French users, EU for others.
   */
  async getRegionForCountry(countryCode: string): Promise<string> {
    // Simple logic: France gets FR, everyone else gets EU
    if (countryCode?.toUpperCase() === "FR") {
      return "FR";
    }
    return "EU"; // Default fallback
  },

  /**
   * Get pricing for a specific region.
   */
  async getPricingForRegion(regionId: string): Promise<Price[]> {
    // France only pricing - fixed prices for now
    const francePrices: Price[] = [
      {
        product_id: "pro_monthly",
        amount: 2900, // 29€ in cents
        currency_symbol: "€",
        lemonsqueezy_variant_id: undefined, // Will be added later
      },
      {
        product_id: "pro_yearly",
        amount: 29000, // 290€ in cents (10 months free)
        currency_symbol: "€",
        lemonsqueezy_variant_id: undefined,
      },
    ];

    // Return France prices for FR region, empty for others (or could return same prices)
    if (regionId === "FR") {
      return francePrices;
    }

    // For EU region, return same prices (since you only sell in France)
    return francePrices;
  },

  /**
   * Get region details.
   */
  async getRegion(regionId: string): Promise<Region | null> {
    if (regionId === "FR") {
      return FRANCE_REGION;
    }

    // EU fallback
    return {
      id: "EU",
      name: "Europe",
      currency_code: "EUR",
      currency_symbol: "€",
    };
  },

  /**
   * Get all supported regions.
   */
  async getAllRegions(): Promise<Region[]> {
    return [
      FRANCE_REGION,
      {
        id: "EU",
        name: "Europe",
        currency_code: "EUR",
        currency_symbol: "€",
      },
    ];
  },

  /**
   * Create a checkout session for a given variant.
   * TODO: Implement when you're ready to sell
   */
  async createCheckoutSession(
    variantId: string,
    email: string,
    returnPath?: string,
  ): Promise<string> {
    // Not implemented yet - you're not selling anything for now
    throw new Error("Checkout not implemented - not selling products yet");

    // Future implementation:
    // const response = await invokeSupabaseFunction<{ url: string }>(
    //   "create-checkout",
    //   { variantId, email, returnPath }
    // );
    // return response.url;
  },
};
